import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import useSecureMediaUrl from "hooks/useSecureMediaUrl";
import { fetchSharedMediaBlobUrl } from "api/secureMedia";
import { toUploadRelativePath } from "utils/mediaUrl";

const PDF_WORKER_URL =
  "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js";

const IMAGE_EXTENSIONS = new Set([
  "png",
  "jpg",
  "jpeg",
  "gif",
  "tif",
  "tiff",
  "webp",
]);

const getExtension = (fileName = "") =>
  String(fileName).split(".").pop()?.toLowerCase() || "";

const isImageFile = (fileName) => IMAGE_EXTENSIONS.has(getExtension(fileName));
const isPdfFile = (fileName) => getExtension(fileName) === "pdf";
const isPreviewableFile = (fileName) =>
  isImageFile(fileName) || isPdfFile(fileName);

const decodeShareFileName = (shareToken) => {
  try {
    const payloadPart = String(shareToken || "").split(".")[0];
    if (!payloadPart) return "";
    const pad =
      payloadPart.length % 4 === 0
        ? ""
        : "=".repeat(4 - (payloadPart.length % 4));
    const b64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/") + pad;
    const payload = JSON.parse(atob(b64));
    const relative = String(payload?.p || "");
    if (!relative || relative.includes("..")) return "";
    return relative.split("/").pop() || "";
  } catch {
    return "";
  }
};

const toolbarButtonStyle = {
  position: "absolute",
  top: 16,
  background: "rgba(0,0,0,0.55)",
  border: "none",
  color: "#fff",
  width: 44,
  height: 44,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  zIndex: 10051,
  fontSize: 18,
  transition: "transform 0.15s ease, background 0.15s ease",
};

const lightboxToolbarHover = {
  onMouseEnter: (e) => {
    e.currentTarget.style.transform = "scale(1.12)";
    e.currentTarget.style.background = "rgba(0,0,0,0.75)";
  },
  onMouseLeave: (e) => {
    e.currentTarget.style.transform = "scale(1)";
    e.currentTarget.style.background = "rgba(0,0,0,0.55)";
  },
};

const SecureMediaPdf = ({ fileUrl, isDarkMode }) => {
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  return (
    <Worker workerUrl={PDF_WORKER_URL}>
      <div style={{ height: "100%", overflow: "auto" }}>
        <Viewer
          fileUrl={fileUrl}
          theme={isDarkMode ? "dark" : "light"}
          defaultScale={1}
          plugins={[defaultLayoutPluginInstance]}
        />
      </div>
    </Worker>
  );
};

const triggerBlobDownload = (blobUrl, fileName) => {
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName || "download";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Media lightbox for:
 * - Internal (login): /media/uploads/*
 * - External share (no login): /media/share/:token
 */
const SecureMediaViewer = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const autoDownloadDoneRef = useRef(false);

  const shareToken = useMemo(() => {
    if (!location.pathname.startsWith("/media/share/")) return "";
    const raw = params.token || "";
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }, [location.pathname, params.token]);

  const isShareMode = !!shareToken;

  const wildcard = params["*"] || "";
  const uploadPath = useMemo(
    () =>
      isShareMode ? "" : toUploadRelativePath(`/uploads/${wildcard}`),
    [isShareMode, wildcard]
  );

  const fileName = useMemo(() => {
    if (isShareMode) return decodeShareFileName(shareToken);
    return uploadPath ? uploadPath.split("/").pop() : "";
  }, [isShareMode, shareToken, uploadPath]);

  const isPdf = isPdfFile(fileName);
  const isPreviewable = isPreviewableFile(fileName);
  const isDarkMode =
    typeof document !== "undefined" &&
    !document.body.classList.contains("white-content");

  const secure = useSecureMediaUrl(uploadPath, {
    enabled: !isShareMode && !!uploadPath,
  });

  const [shareBlobUrl, setShareBlobUrl] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState(null);

  useEffect(() => {
    if (!isShareMode || !shareToken) {
      setShareBlobUrl("");
      setShareLoading(false);
      setShareError(null);
      return undefined;
    }

    let cancelled = false;
    let createdUrl = "";
    setShareLoading(true);
    setShareError(null);

    fetchSharedMediaBlobUrl(shareToken)
      .then((result) => {
        if (cancelled) {
          URL.revokeObjectURL(result.blobUrl);
          return;
        }
        createdUrl = result.blobUrl;
        setShareBlobUrl(result.blobUrl);
      })
      .catch((err) => {
        if (cancelled) return;
        setShareBlobUrl("");
        setShareError(err);
      })
      .finally(() => {
        if (!cancelled) setShareLoading(false);
      });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [isShareMode, shareToken]);

  const blobUrl = isShareMode ? shareBlobUrl : secure.blobUrl;
  const isLoading = isShareMode ? shareLoading : secure.isLoading;
  const error = isShareMode ? shareError : secure.error;
  const hasTarget = isShareMode ? !!shareToken : !!uploadPath;

  const handleClose = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(isShareMode ? "/" : "/dashboard", { replace: true });
  }, [navigate, isShareMode]);

  const handleDownload = useCallback(() => {
    if (!blobUrl) return;
    triggerBlobDownload(blobUrl, fileName);
  }, [blobUrl, fileName]);

  useEffect(() => {
    if (isPreviewable || !blobUrl || isLoading || error) return;
    if (autoDownloadDoneRef.current) return;
    autoDownloadDoneRef.current = true;
    triggerBlobDownload(blobUrl, fileName);
    const timer = window.setTimeout(() => {
      handleClose();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [isPreviewable, blobUrl, isLoading, error, fileName, handleClose]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [handleClose]);

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={fileName || "Media"}
      onClick={isPreviewable ? handleClose : undefined}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.95)",
        zIndex: 99999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: 0,
        padding: 0,
      }}
    >
      {isPreviewable && blobUrl && !isLoading && !error ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleDownload();
          }}
          style={{ ...toolbarButtonStyle, right: 72 }}
          aria-label="Download"
          title="Download"
          {...lightboxToolbarHover}
        >
          <i className="tim-icons icon-cloud-download-93" />
        </button>
      ) : null}

      {isPreviewable ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          style={{ ...toolbarButtonStyle, right: 16 }}
          aria-label="Close"
          title="Close"
          {...lightboxToolbarHover}
        >
          <i className="tim-icons icon-simple-remove" />
        </button>
      ) : null}

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: isPdf ? "90vw" : "auto",
          height: isPdf ? "85vh" : "auto",
          maxWidth: "92vw",
          maxHeight: "90vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {!hasTarget && (
          <div style={{ color: "#fff", padding: 24, textAlign: "center" }}>
            Invalid media path.
          </div>
        )}

        {hasTarget && isLoading && (
          <div style={{ color: "#fff", textAlign: "center" }}>
            <div className="spinner-border mb-3" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <div>
              {isPreviewable ? "Loading..." : "Downloading..."}
            </div>
          </div>
        )}

        {hasTarget && error && !isLoading && (
          <div style={{ color: "#fff", padding: 24, textAlign: "center", maxWidth: 420 }}>
            Could not load this file. You may not be authorized, or the file is missing.
          </div>
        )}

        {blobUrl && !isLoading && !error && !isPreviewable && (
          <div style={{ color: "#fff", textAlign: "center" }}>
            <div className="spinner-border mb-3" role="status">
              <span className="sr-only">Downloading...</span>
            </div>
            <div>Downloading {fileName || "file"}...</div>
          </div>
        )}

        {blobUrl && !isLoading && !error && isPreviewable && (
          isPdf ? (
            <div
              style={{
                width: "90vw",
                height: "85vh",
                background: isDarkMode ? "#1e1e2d" : "#fff",
                overflow: "hidden",
              }}
            >
              <SecureMediaPdf fileUrl={blobUrl} isDarkMode={isDarkMode} />
            </div>
          ) : (
            <img
              src={blobUrl}
              alt={fileName || "Media"}
              style={{
                maxWidth: "90vw",
                maxHeight: "85vh",
                objectFit: "contain",
                background: "#111",
              }}
            />
          )
        )}
      </div>

      {isPreviewable && fileName ? (
        <div
          style={{
            position: "absolute",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            textAlign: "center",
            maxWidth: "80vw",
            pointerEvents: "none",
          }}
        >
          {fileName}
        </div>
      ) : null}
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default SecureMediaViewer;
